import React, { HTMLAttributes, useRef, useState } from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  Placement,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';

type Props = {
  showDelay?: number;
  trigger: React.ReactElement;
  children?: React.ReactElement;
  position?: Placement;
  width?: string;
  disabled?: boolean;
  height?: string;
  fixedFloating?: boolean;
  showOnClick?: boolean;
  bgColor?: string;
  fillColor?: string;
  className?: string;
  small?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const WithTooltip: React.FC<Props> = ({
  showDelay = 0,
  trigger,
  children,
  position = 'top',
  width = '17.188rem',
  height = 'auto',
  disabled = false,
  fixedFloating,
  bgColor = 'ctn-neutral-black',
  fillColor,
  className,
  small,
}) => {
  const arrowWidth = small ? 8 : 16;
  const arrowHeight = arrowWidth / 2;
  const [isOpen, setIsOpen] = useState(false);

  const arrowRef = useRef(null);

  const { refs, floatingStyles, context, placement, middlewareData } = useFloating({
    placement: position,
    strategy: fixedFloating ? 'fixed' : 'absolute',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      flip(),
      shift(),
      offset({
        mainAxis: 4,
        crossAxis: 0,
      }),
      arrow({
        element: arrowRef,
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: {
      open: showDelay,
      close: 0,
    },

    handleClose: safePolygon(),
    move: false,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'tooltip',
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const getArrowStyle = (): object => {
    const hasShifted = !!middlewareData.shift?.x || !!middlewareData.shift?.y;

    if (hasShifted) {
      return {};
    }

    if (placement.endsWith('start')) {
      return {
        left: '8px',
      };
    }
    if (placement.endsWith('end')) {
      return {
        right: '8px',
        left: 'auto',
      };
    }
    return {};
  };

  return children && !disabled ? (
    <>
      {React.cloneElement(trigger, {
        ref: refs.setReference,
        ...getReferenceProps(),
      } as HTMLAttributes<HTMLElement>)}
      {isOpen && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          className={classNames(
            'absolute z-50 text-inverse rounded-xxs flex items-center justify-center text-left whitespace-pre-wrap break-words',
            small ? 'py-xxs px-xs' : 'py-xs px-sm',
            bgColor,
            className,
          )}
          //eslint-disable-next-line
          style={{
            ...floatingStyles,
            maxWidth: `${width}`,
            height: `${height}`,
          }}
        >
          {children}
          <FloatingArrow
            className={fillColor}
            ref={arrowRef}
            context={context}
            height={arrowHeight}
            width={arrowWidth}
            //eslint-disable-next-line
            style={getArrowStyle()}
          />
        </div>
      )}
    </>
  ) : (
    trigger
  );
};
